import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence
} from 'firebase/auth';
import { auth } from '../config/firebase';
import UserService from '../utils/services/UserService';
import useGoogleAnalytics from '../hooks/useGoogleAnalytics';
import firebaseService from '../utils/services/FirebaseService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  
  // Google Analytics tracking
  const { trackLogin, trackLogout, trackSignUp, trackError } = useGoogleAnalytics();

  // Get user data from Firestore using UserService
  const getUser = async (uid) => {
    try {
      const userData = await UserService.getUser(uid, true);
      if (userData) {
        setCurrentUserData(userData);
        return userData;
      } else {
        // Create a default user data if the document doesn't exist
        console.log('User document not found, creating default user data');
        const defaultUserData = {
          key: uid,
          uid: uid,
          username: currentUser?.email,
          displayName: currentUser?.displayName || currentUser?.email?.split('@')[0] || 'User',
          created: Date.now()
        };
        setCurrentUserData(defaultUserData);
        return defaultUserData;
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      // Create a default user data on error
      const defaultUserData = {
        key: uid,
        uid: uid,
        username: currentUser?.email,
        displayName: currentUser?.displayName || currentUser?.email?.split('@')[0] || 'User',
        created: Date.now()
      };
      setCurrentUserData(defaultUserData);
      return defaultUserData;
    }
  };

  // Get all users (admin only) using UserService
  const getUsers = async () => {
    if (currentUserData && UserService.isUserAdmin(currentUserData)) {
      try {
        const usersData = await UserService.getAllUsers(true);
        setUsers(usersData);
        return usersData;
      } catch (error) {
        console.error('Error fetching users:', error);
        return [];
      }
    } else {
      setUsers([]);
      return [];
    }
  };

  // Sign in with email and password
  const signIn = async (email, password, keepSignedIn = false) => {
    try {
      await setPersistence(auth, keepSignedIn ? browserLocalPersistence : browserSessionPersistence);
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      // Track successful login
      trackLogin('email');
      
      return result;
    } catch (error) {
      console.error('Sign in error:', error);
      
      // Track login error
      trackError('login_error', error.message, error.code);
      
      // Handle user-not-found error by checking for orphaned data
      if (error.code === 'auth/user-not-found') {
        try {
          console.log('User not found in Firebase Auth, checking for orphaned data...');
          
          // Check if there's orphaned user data in Firestore
          const wasCleanedUp = await UserService.cleanupOrphanedUser(email);
          
          if (wasCleanedUp) {
            console.log('Orphaned user data cleaned up. User should sign up again.');
            throw new Error('User account was deleted. Please sign up again to create a new account.');
          } else {
            console.log('No orphaned data found. User simply does not exist.');
            throw new Error('No account found with this email. Please check your email or sign up for a new account.');
          }
        } catch (cleanupError) {
          console.error('Error during orphaned data cleanup:', cleanupError);
          // If cleanup fails, provide a generic error message
          throw new Error('Account not found. Please check your email or contact support if you believe this is an error.');
        }
      }
      
      throw error;
    }
  };

  // Sign up with email and password
  const signUp = async (email, password, displayName, keepSignedIn = false) => {
    try {
      await setPersistence(auth, keepSignedIn ? browserLocalPersistence : browserSessionPersistence);
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update profile with display name
      await updateProfile(result.user, { displayName });
      
      // Create user document in Firestore using UserService
      const userData = {
        uid: result.user.uid,
        username: result.user.email,
        displayName: displayName, // Use the displayName parameter directly
        demoUser: false,
        emailUnsubscribed: false,
        isAdmin: false,
        testUser: false
      };
      
      await UserService.createUser(userData);

      // Force token refresh after user creation to ensure proper permissions
      try {
        await result.user.getIdToken(true);
        console.log('Token refreshed after user creation');
      } catch (tokenError) {
        console.error('Error refreshing token after user creation:', tokenError);
        // Don't throw error here, as signup was successful
      }

      // Send email verification automatically after signup
      try {
        await sendEmailVerification(result.user);
        console.log('Email verification sent successfully after signup');
        setEmailVerificationSent(true);
      } catch (verificationError) {
        console.error('Error sending email verification after signup:', verificationError);
        // Don't throw error here, as signup was successful
      }

      // Track successful signup
      trackSignUp('email');

      // Send admin notification email about new user creation
      try {
        // firebaseService is now imported statically
        await firebaseService.sendAdminEmail(
          "New user created",
          `New user '${displayName}' (${email}) has been created`,
          {
            title: "New user created",
            text: `New user '${displayName}' (${email}) has been created`,
          }
        );
        console.log('Admin notification email sent successfully');
      } catch (adminEmailError) {
        console.error('Error sending admin notification email:', adminEmailError);
        // Don't throw error here, as signup was successful
      }

      return result;
    } catch (error) {
      console.error('Sign up error:', error);
      throw error;
    }
  };

  // Sign out
  const logout = async () => {
    try {
      await signOut(auth);
      
      // Track successful logout
      trackLogout();
    } catch (error) {
      console.error('Sign out error:', error);
      
      // Track logout error
      trackError('logout_error', error.message, error.code);
      
      throw error;
    }
  };

  // Send password reset email
  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  };

  // Send email verification
  const verifyEmail = async () => {
    try {
      await sendEmailVerification(auth.currentUser);
      setEmailVerificationSent(true);
    } catch (error) {
      console.error('Email verification error:', error);
      throw error;
    }
  };

  // Refresh authentication token
  const refreshToken = async () => {
    try {
      if (auth.currentUser) {
        await auth.currentUser.getIdToken(true);
        console.log('Authentication token refreshed');
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      throw error;
    }
  };

  // Resend email verification
  const resendEmailVerification = async () => {
    try {
      if (!auth.currentUser) {
        throw new Error('No authenticated user found');
      }
      await sendEmailVerification(auth.currentUser);
      setEmailVerificationSent(true);
    } catch (error) {
      console.error('Error resending email verification:', error);
      throw error;
    }
  };

  // Update user profile using UserService
  const updateUser = async (userData) => {
    try {
      // Update user in Firestore using UserService
      await UserService.updateUser(currentUserData.key, userData);
      
      // Update Firebase Auth profile if display name changed
      if (userData.displayName) {
        await updateProfile(auth.currentUser, { displayName: userData.displayName });
      }
      
      // Refresh user data
      await getUser(currentUser.uid);
      if (UserService.isUserAdmin(currentUserData)) {
        await getUsers();
      }
    } catch (error) {
      console.error('Update user error:', error);
      throw error;
    }
  };

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('Auth state changed:', user ? 'User logged in' : 'User logged out');
      setCurrentUser(user);
      if (user) {
                  try {
            // Force token refresh to ensure email verification status is up to date
            await user.getIdToken(true);
            console.log('Token refreshed, email verified status:', user.emailVerified);
            
            // Add a small delay to ensure Firestore operations are complete
            await new Promise(resolve => setTimeout(resolve, 1500));
          
          console.log('Loading user data for:', user.uid);
          let userData = null;
          try {
            userData = await getUser(user.uid);
            console.log('User data loaded:', userData);
          } catch (userError) {
            console.log('Could not load user data during auth state change, using default:', userError.message);
            // Create a default user data object
            userData = {
              key: user.uid,
              uid: user.uid,
              username: user.email,
              displayName: user.displayName || user.email?.split('@')[0] || 'Unknown User',
              emailVerified: user.emailVerified,
              isAdmin: false,
              testUser: false,
              demoUser: false,
              created: Date.now()
            };
          }
          
          // Check if email is verified (except for admin users and test users)
          if (!user.emailVerified && !UserService.isUserAdmin(userData) && !UserService.isUserTest(userData)) {
            console.log('User email not verified, preventing access');
            setCurrentUserData(null);
            setUsers([]);
            setLoading(false);
            return;
          } else if (user.emailVerified) {
            // Reset email verification sent state when email is verified
            setEmailVerificationSent(false);
            
            // Update user document with email verification status if not already verified
            if (!userData?.emailVerified) {
              try {
                const updateResult = await UserService.updateEmailVerificationStatus(user.uid, true);
                if (updateResult) {
                  console.log('Updated user document with email verification status');
                  
                  // Send admin notification when user verifies email (only once)
                  if (!UserService.isUserAdmin(userData)) {
                    try {
                      // firebaseService is now imported statically
                      await firebaseService.sendAdminEmail(
                        "User email verified",
                        `User '${userData?.displayName || user.displayName || user.email?.split('@')[0] || 'Unknown User'}' (${user.email}) has verified their email`,
                        {
                          title: "User email verified",
                          text: `User '${userData?.displayName || user.displayName || user.email?.split('@')[0] || 'Unknown User'}' (${user.email}) has verified their email`,
                        }
                      );
                      console.log('Admin notification email sent for email verification');
                    } catch (adminEmailError) {
                      console.error('Error sending admin notification email for verification:', adminEmailError);
                      // Don't throw error here, as verification was successful
                    }
                  }
                } else {
                  console.log('Email verification status update skipped (user document not found or update failed)');
                }
              } catch (updateError) {
                console.error('Error updating email verification status:', updateError);
                // Don't throw error here, as verification was successful
              }
            }
          }
          
          // Set the current user data
          setCurrentUserData(userData);
          
          if (UserService.isUserAdmin(userData)) {
            await getUsers();
          }
        } catch (error) {
          console.error('Error loading user data:', error);
          // Set a default user data even on error
          const defaultUserData = {
            key: user.uid,
            uid: user.uid,
            username: user.email,
            displayName: user.displayName || user.email?.split('@')[0] || 'Unknown User',
            emailVerified: user.emailVerified,
            isAdmin: false,
            testUser: false,
            demoUser: false,
            created: Date.now()
          };
          setCurrentUserData(defaultUserData);
        } finally {
          setLoading(false);
        }
      } else {
        setCurrentUserData(null);
        setUsers([]);
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    currentUserData,
    users,
    loading,
    emailVerificationSent,
    signIn,
    signUp,
    logout,
    resetPassword,
    verifyEmail,
    refreshToken,
    resendEmailVerification,
    updateUser,
    getUsers,
    getUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 